import { createSelector } from 'reselect';

import { UserType } from 'ha/constants/UserType';
import { getDay, getMonth, getYear } from 'ha/helpers/hadate';
import { GlobalState } from 'ha/types/store';

import { LegacyUser } from './types';

const getAuth = (s: GlobalState) => s.authLogic;

const getUserV2 = (s: GlobalState) => s.authLogic.user;

const getUserInformation = (s: GlobalState) => s.authLogic.userInformation;

export const getUserFullName = (s: GlobalState) => {
  const user = getUserV2(s);

  if (!user) {
    return null;
  }

  return `${user.firstName} ${user.lastName}`;
};

export const getUser = createSelector(
  [getUserV2, getUserInformation, getUserFullName],
  (user, userInformation, fullName) => {
    if (!user || !userInformation) {
      return null;
    }

    const legacyUser: LegacyUser = {
      Name: fullName!,
      isAdmin: user.isAdmin,
      isPartner: user.isPartner,
      ID: user.id,
      avatarURL: user.avatarURL,
      birthDate: user.birthDate,
      CompletedBookings: userInformation.advertiserBookingsCount,
      CountryCode: user.countryCode,
      description: user.description,
      Email: user.email,
      EmailVerified: user.isEmailVerified,
      FirstName: user.firstName,
      HasFacebook: user.hasFacebookProfile,
      HasPaymentMethod: Boolean(
        userInformation.savedPaymentMethodsCount &&
          userInformation.savedPaymentMethodsCount > 0,
      ),
      HasPhone: user.hasPhone,
      LastActivityAt: user.lastActivityAt,
      LastName: user.lastName,
      ListingsCount: userInformation.listingsCount,
      Location: user.location,
      nationality: user.nationality,
      Phone: user.phone,
      RegistrationDate: user.createdAt,
      ResponseRate: user.responseRate,
      ResponseTime: user.responseTime,
      SpokenLanguages: user.spokenLanguages,
      study: user.study,
      Type: user.type,
      uniID: user.uniId,
      UniID: user.uniId,
      work: user.work,
      isAliased: user.isAliased,
      isTrusted: user.isTrusted,
    };

    return legacyUser;
  },
);

export const getIsLoading = (s: GlobalState) => getAuth(s).isLoading;
export const getIsLoaded = (s: GlobalState) => getAuth(s).isLoaded;

export const getCurrentLanguage = (s: GlobalState) => getAuth(s).language;

export const getIsAuthenticated = (s: GlobalState) =>
  getAuth(s).isAuthenticated;

export const getUserPhone = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.phone : null;

export const getUserFirstName = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.firstName : null;

export const getUserLastName = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.lastName : null;
export const getUserType = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.type : null;
export const getUserEmail = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.email : null;
export const getCountryCode = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.countryCode : null;
export const getUserId = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.id : null;
export const getIsAdmin = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isAdmin : false;
export const getIsPartner = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isPartner : null;
export const getUserUniversity = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.study : null;
export const getUserBirthDate = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.birthDate : null;
export const getUserBirthDay = (s: GlobalState) => {
  const date = getUserBirthDate(s);
  if (!date) {
    return date;
  }
  return getDay(date);
};
export const getUserBirthMonth = (s: GlobalState) => {
  const date = getUserBirthDate(s);
  if (!date) {
    return date;
  }
  return getMonth(date) + 1;
};
export const getUserBirthYear = (s: GlobalState) => {
  const date = getUserBirthDate(s);
  if (!date) {
    return date;
  }
  return getYear(date);
};
export const getUserSpokenLanguages = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.spokenLanguages : [];
export const getIsAliased = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isAliased : false;

export const getUserNationality = (s: GlobalState) => {
  const user = s.authLogic.user;
  if (!user) {
    return user;
  }
  return /^[A-Z]{2}$/.test(user.nationality || '') ? user.nationality : '';
};

export const getUserAvatar = (s: GlobalState) =>
  s.authLogic.user && s.authLogic.user.avatarURL;

export const getResponseRate = createSelector(
  [getUser],
  user => (user ? Math.round(user.ResponseRate * 100) : 0),
);

export const isResponseRateDisplaying = createSelector(
  [getUser],
  user => user && !!(user.ListingsCount && user.ResponseRate),
);

export const getIsVerified = createSelector(
  [getIsAuthenticated, getUserType, getIsAdmin],
  (isAuthenticated, userType, isAdmin) =>
    isAuthenticated &&
    ([UserType.STUDENT, UserType.LANDLORD].includes(userType!) || isAdmin),
);

export const getIsUnderReview = createSelector(
  [getUserType],
  userType => userType === UserType.UNDER_REVIEW,
);

export const getUserIdAlias = createSelector(
  [getUserId],
  userId => `u${userId}`,
);

export const getIsUserInformationLoading = (s: GlobalState) =>
  s.authLogic.isUserBillingInfoLoading;

export const hasCompletedBookings = (s: GlobalState) =>
  s.authLogic.userInformation &&
  s.authLogic.userInformation.advertiserBookingsCount > 0;

export const isMissingBillingDetails = (s: GlobalState) =>
  s.authLogic.userInformation &&
  s.authLogic.userInformation.hasOwnProperty('hasCompleteBillingInformation') &&
  !s.authLogic.userInformation.hasCompleteBillingInformation;

export const isMissingPayoutMethod = (s: GlobalState) =>
  s.authLogic.userInformation &&
  s.authLogic.userInformation.savedPayoutMethodsCount === 0;

export const getHasOtherListings = (s: GlobalState) =>
  s.authLogic.userInformation && s.authLogic.userInformation.listingsCount > 1;

export const getHasListings = (s: GlobalState) =>
  (s.authLogic.userInformation &&
    s.authLogic.userInformation.listingsCount > 0) ||
  false;

export const isAuthenticatedWithBookings = (s: GlobalState) =>
  getIsAuthenticated(s) && hasCompletedBookings(s);

export const getImpersonatingAdmin = (s: GlobalState) =>
  s.authLogic.impersonatingAdmin;

export const getUserTraits = createSelector(
  [getUser, getIsVerified, getIsAdmin, getImpersonatingAdmin],
  (user, isVerified, isAdmin, isImpersonated) =>
    user && {
      firstName: user.FirstName,
      lastName: user.LastName,
      email: user.Email,
      usertype: user.Type,
      phone: user.Phone,
      createdAt: user.RegistrationDate,
      verified: isVerified,
      isAdmin,
      isImpersonated: Boolean(isImpersonated),
    },
);
